import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2, MessageSquare, Pin, FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

export function RecentPostsTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadRecentPosts = async () => {
      try {
        setLoading(true);

        // Get all projects the user has access to
        const { data: userProjects } = await supabase
          .from('team_members')
          .select('project_id')
          .eq('user_id', userProfile?.id);

        const projectIds = (userProjects || []).map(p => p.project_id);

        if (projectIds.length === 0) {
          setPosts([]);
          setLoading(false);
          return;
        }

        // Load recent posts from accessible projects
        const { data: postsData, error: postsError } = await supabase
          .from('posts')
          .select(`
            id,
            title,
            content,
            pinned,
            post_type,
            created_date,
            author_id,
            project_id,
            user_profiles!posts_author_id_fkey(
              first_name,
              last_name
            ),
            projects!posts_project_id_fkey(
              name
            )
          `)
          .in('project_id', projectIds)
          .order('created_date', { ascending: false })
          .limit(10);

        if (postsError) throw postsError;

        setPosts(postsData || []);
      } catch (error) {
        console.error('Failed to load recent posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRecentPosts();
  }, [company?.id, userProfile?.id]);

  const goToPosts = () => {
    navigate('/posts');
  };

  const goToPost = (post) => {
    navigate(`/space/${post.project_id}/message-board`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <MessageSquare className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900">No posts yet</p>
        <p className="text-xs text-gray-500 mt-1">Posts will appear here</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b shrink-0">
        <span className="text-xs text-gray-600">{posts.length} recent posts</span>
      </div>

      {/* Posts list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {posts.map((post) => (
            <div
              key={post.id}
              onClick={() => goToPost(post)}
              className={cn(
                "p-2 rounded-lg border bg-gray-50 border-gray-200",
                "hover:shadow-md cursor-pointer transition-all"
              )}
            >
              <div className="flex items-start gap-2">
                {/* Icon */}
                <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  {post.pinned ? (
                    <Pin className="h-3.5 w-3.5 text-violet-600" />
                  ) : (
                    <FileText className="h-3.5 w-3.5 text-violet-600" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-medium text-gray-900 truncate">
                      {post.title}
                    </span>
                    {post.pinned && (
                      <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">
                        Pinned
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                    <span className="truncate">
                      {post.user_profiles?.first_name} {post.user_profiles?.last_name}
                    </span>
                    <span>•</span>
                    <span className="truncate">{post.projects?.name}</span>
                  </div>
                </div>

                {/* Time */}
                <span className="text-[10px] text-gray-400 shrink-0">
                  {formatDistanceToNow(new Date(post.created_date), { addSuffix: true })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Footer */}
      <button
        onClick={goToPosts}
        className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors shrink-0"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        <span>View All Posts</span>
      </button>
    </div>
  );
}
